import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ComplexPersonCondition, ConditionalKeys, DEFAULT_KEYS, PersonCondition, PersonMatcher, SIMPLE_CONDITION_OPTIONS, SimplePersonCondition } from '@weavix/models/src/person/person-matcher';
import { PersonSelect } from '@weavix/models/src/person/person-select';
import { Chip } from 'components/chip-list/chip-list.component';
import { DropdownItem } from 'components/dropdown/dropdown.model';
import { isEqual } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { CompanyService } from 'weavix-shared/services/company.service';
import { CraftService } from 'weavix-shared/services/craft.service';
import { FacilityService } from 'weavix-shared/services/facility.service';
import { PersonImportService } from 'weavix-shared/services/person-import.service';
import { PersonService } from 'weavix-shared/services/person.service';
import { ProfileService } from 'weavix-shared/services/profile.service';
import { TagService } from 'weavix-shared/services/tag.service';

export type SimpleConditionForm = FormGroup<{
    key: FormControl<ConditionalKeys>;
    negate: FormControl<boolean>;
    value: FormControl<string[]>;
    self: FormControl<boolean>;
}>;

export type ComplexConditionForm = FormGroup<{
    conditions: FormArray<SimpleConditionForm | ComplexConditionForm>;
    type: FormControl<'and' | 'or'>;
}>;

export type NegateOptions = Partial<Record<ConditionalKeys, Array<DropdownItem & { control?: FormGroup }>>>;

export interface MatcherOptions {
    includes?: ConditionalKeys[];
    people?: PersonSelect[];
    includeCraftsWithoutFacility?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class PersonMatcherService {
    allValues: Record<ConditionalKeys, Chip[]>;
    formValues: Record<ConditionalKeys, Chip[]>;
    key$ = new BehaviorSubject<DropdownItem[]>([]);
    negate$ = new BehaviorSubject<NegateOptions>({});

    allPeople: PersonSelect[] = [];
    self: PersonSelect;

    constructor(
        private readonly personService: PersonService,
        private readonly personImportService: PersonImportService,
        private readonly companyService: CompanyService,
        private readonly tagService: TagService,
        private readonly craftService: CraftService,
        private readonly facilityService: FacilityService,
        private readonly profileService: ProfileService,
    ) { }

    invalidateCache() {
        this.allValues = null;
        this.formValues = null;
        this.allPeople = [];
        this.self = null;
    }

    async loadValues() {
        if (this.allValues) return;

        const [peopleResult, pendingPeople, requestedPeople, importedPeople] = await Promise.all([
            this.personService.getPeople(null),
            this.personService.getPendingPeople(null),
            this.personService.getRequestedPeople(null),
            this.personImportService.getImportedPeople(null),
        ]);
        const people = [...peopleResult, ...pendingPeople, ...requestedPeople, ...importedPeople];

        const [facilities, crafts, tags, companies] = await Promise.all([
            this.facilityService.getAll(null),
            this.craftService.getAll(null),
            this.tagService.getAll(null),
            this.companyService.getAllOnAccount(null),
        ]);

        this.allPeople = people;
        const person = await this.profileService.getUserProfile(null);
        this.self = people.find(v => v.id === person.id);

        this.allValues = {
            [ConditionalKeys.People]: people.map(person => ({ id: person.id, name: person.fullName })),
            [ConditionalKeys.PersonSites]: facilities.map(site => ({ id: site.id, name: site.name })),
            [ConditionalKeys.PersonCraft]: crafts.map(craft => ({ id: craft.id, name: craft.name })),
            [ConditionalKeys.PersonTags]: tags.map(tag => ({ id: tag.id, name: tag.name })),
            [ConditionalKeys.PersonCompany]: companies.map(company => ({ id: company.id, name: company.name })),
        };

        this.formValues = this.allValues;
    }

    filterPeople(people: PersonSelect[], includeCraftsWithoutFacility = false) {
        const facilities = new Set<string>();
        const companies = new Set<string>();
        const tags = new Set<string>();
        const crafts = new Set<string>();
        people.forEach(person => {
            Object.keys(person.facilities ?? {}).forEach(facilityId => facilities.add(facilityId));
            person.crafts?.forEach(craft => crafts.add(craft));
            person.tags?.forEach(tag => tags.add(tag));
            companies.add(person.companyId);
        });

        // A craft belongs to a facility only through the people assigned to it, so crafts nobody
        // on a facility has are "not assigned to any site" and stay selectable when opted in.
        const facilityCrafts = new Set<string>();
        if (includeCraftsWithoutFacility) {
            this.allPeople.forEach(person => {
                if (Object.keys(person.facilities ?? {}).length) person.crafts?.forEach(craft => facilityCrafts.add(craft));
            });
        }

        this.formValues[ConditionalKeys.People] = people.map(person => ({ id: person.id, name: person.fullName }));
        this.formValues[ConditionalKeys.PersonSites] = this.allValues[ConditionalKeys.PersonSites].filter(v => facilities.has(v.id));
        this.formValues[ConditionalKeys.PersonCompany] = this.allValues[ConditionalKeys.PersonCompany].filter(v => companies.has(v.id));
        this.formValues[ConditionalKeys.PersonCraft] = this.allValues[ConditionalKeys.PersonCraft]
            .filter(v => crafts.has(v.id) || (includeCraftsWithoutFacility && !facilityCrafts.has(v.id)));
        this.formValues[ConditionalKeys.PersonTags] = this.allValues[ConditionalKeys.PersonTags].filter(v => tags.has(v.id));
    }

    initializeForm(form: FormGroup, value: PersonMatcher, options?: MatcherOptions) {
        if (options?.people) this.filterPeople(options.people, options.includeCraftsWithoutFacility);
        else this.formValues = this.allValues;
        if (this.formValues && value) this.removeInvalidValues(value);
        if (!form.get('conditions')) {
            const advanced = new FormGroup({
                conditions: new FormArray((value?.conditions?.conditions ?? []).map(v => this.getFormGroup(v))),
                type: new FormControl(value?.conditions?.type ?? 'and'),
            });
            form.addControl('conditions', advanced);
        }
    }

    private getFormGroup(condition: PersonCondition): SimpleConditionForm | ComplexConditionForm {
        if ((condition as SimplePersonCondition).key) {
            const simple = condition as SimplePersonCondition;
            return new FormGroup({
                key: new FormControl(simple.key),
                negate: new FormControl(simple.negate),
                value: new FormControl(simple.value),
                self: new FormControl(simple.self ?? false),
            });
        } else {
            const complex = condition as ComplexPersonCondition;
            return new FormGroup({
                conditions: new FormArray((complex?.conditions ?? []).map(v => this.getFormGroup(v))),
                type: new FormControl(complex?.type ?? 'and'),
            });
        }
    }

    configureFilterDropdownOptions(includes = DEFAULT_KEYS, includeSelf = false) {
        const key: DropdownItem[] = [];
        const negate: NegateOptions = {};
        Object.values(ConditionalKeys).forEach(conditionalKey => {
            if (!includes.includes(conditionalKey)) return;

            const label = {
                [ConditionalKeys.People]: 'rField.people.people',
                [ConditionalKeys.PersonCompany]: 'rField.people.withCompany',
                [ConditionalKeys.PersonCraft]: 'rField.people.withCraft',
                [ConditionalKeys.PersonTags]: 'rField.people.taggedAs',
                [ConditionalKeys.PersonSites]: 'rField.people.withSite',
            }[conditionalKey];
            key.push({
                key: conditionalKey,
                label,
            });
            negate[conditionalKey] = [
                { key: 'include', label: 'rField.filters.include' },
                { key: 'exclude', label: 'rField.filters.exclude' },
            ];
            if (includeSelf) {
                negate[conditionalKey].push({ key: 'self', label: 'rField.filters.self' });
            }
        });
        if (!isEqual(this.key$.value, key) || !isEqual(this.negate$.value, negate)) {
            this.key$.next(key);
            this.negate$.next(negate);
        }
    }

    removeInvalidValues(value: PersonMatcher, excludeKeys?: ConditionalKeys[]) {
        const removeValues = (condition: PersonCondition) => {
            if ((condition as SimplePersonCondition).key) {
                const simple = condition as SimplePersonCondition;
                if (excludeKeys?.includes(simple.key)) return;
                simple.value = simple.value.filter(v => this.allValues[simple.key].some(x => x.id === v));
            } else {
                const complex = condition as ComplexPersonCondition;
                complex?.conditions.forEach(condition => removeValues(condition));
            }
        };

        // TODO: Some conditionalNotificationOverrides.conditions may still be of type SimplePersonCondition[].
        // These need to be converted to ComplexPersonCondition via an alter.
        // See 1-person-matcher.ts > migrateConditions at 7e70b945c377cb1dcde67b9f418fe38e12ec7dab
        // to detect and convert any remaining SimplePersonCondition entries.
        // JIRA ticket: https://weavix.atlassian.net/browse/WK-4219
        if (value.conditions?.conditions) {
            removeValues(value.conditions);
        }

        // remove invalid standard conditions
        const conditionFields = {
            [ConditionalKeys.People]: ['people', 'excludedPeople'],
            [ConditionalKeys.PersonTags]: ['peopleTags', 'excludedTags'],
            [ConditionalKeys.PersonCraft]: ['peopleCrafts', 'excludedCrafts'],
            [ConditionalKeys.PersonCompany]: ['companies', 'excludedCompanies'],
            [ConditionalKeys.PersonSites]: ['facilityIds', 'excludedFacilityIds'],
        } as const;
        const matcher = value as PersonMatcher & Record<string, string[] | undefined>;
        SIMPLE_CONDITION_OPTIONS.forEach(condition => {
            if (!excludeKeys?.includes(condition.key)) {
                const field = conditionFields[condition.key][condition.negate ? 1 : 0];
                matcher[field] = matcher[field]?.filter(v => this.allValues[condition.key].some(x => x.id === v));
            }
        });
    }

    removeInvalidConditions(value: PersonMatcher, excludeKeys?: ConditionalKeys[]) {
        const keep = (condition: PersonCondition) => {
            if ((condition as SimplePersonCondition)?.key) {
                return !excludeKeys?.includes((condition as SimplePersonCondition).key)
                    && (!!(condition as SimplePersonCondition).value?.length
                        || (condition as SimplePersonCondition).self);
            }
            const complex = condition as ComplexPersonCondition;
            complex.conditions = complex.conditions.filter(condition => keep(condition));
            return !!complex.conditions.length;
        };
        keep(value.conditions);
    }
}
